import { Link } from 'react-router-dom';
import { FaClipboardList, FaCog } from 'react-icons/fa';
import Button from '@/components/UI/Button';
import DeleteButton from '@/components/UI/DeleteButton';
import FormSwitch from '@/components/UI/FormSwitch';
import StatusBadge from '@/components/UI/StatusBadge';
import Tooltip from '@/components/UI/Tooltip';
import {
    canSelectNativeResearchSubscription,
    describeAllowedEntityTypes,
    describeStrategyInstrumentScope,
    STRATEGY_SUBSCRIPTION_STATUS_LABELS,
    STRATEGY_SUBSCRIPTION_STATUS_TONES,
    type StrategySubscriptionPortfolio,
    type StrategySubscriptionRow
} from './strategySubscription';

interface RowProps {
    row: StrategySubscriptionRow;
}

interface RowActionProps extends RowProps {
    onConfigure: (row: StrategySubscriptionRow) => void;
    onManageScorecards: (row: StrategySubscriptionRow) => void;
    /** Omitted where subscriptions cannot be removed, which hides the remove button. */
    onRemove?: (row: StrategySubscriptionRow) => void;
}

/**
 * Status badge plus the activation switch. The switch is disabled — with the blocking reasons on
 * hover — whenever the deployment is incomplete, so an impossible activation is explained before the
 * click rather than rejected after it. An inactive strategy also says so without a hover, since that is
 * the case most easily mistaken for a working subscription.
 */
export const ActivationCell = ({ row, onToggle }: RowProps & { onToggle: (row: StrategySubscriptionRow) => void }) => {
    const { readiness, strategy } = row;
    const researchSelection = canSelectNativeResearchSubscription(row.portfolio, strategy, readiness);
    const canToggle = !row.isTogglePending && (row.isActive || readiness.isDeploymentComplete || researchSelection);

    const activationSwitch = (
        <FormSwitch
            noMargin
            ariaLabel={
                researchSelection
                    ? `${row.isActive ? 'Exclude' : 'Include'} ${strategy.name} in research comparisons`
                    : `${row.isActive ? 'Deactivate' : 'Activate'} ${strategy.name}`
            }
            checked={row.isActive}
            disabled={!canToggle}
            id={`strategy-subscription-${row.key}-active`}
            onChange={() => onToggle(row)}
        />
    );

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
                {canToggle ? activationSwitch : <Tooltip content={readiness.blockingReasons.join('\n')}>{activationSwitch}</Tooltip>}
                <StatusBadge
                    pulse={row.isTogglePending}
                    status={STRATEGY_SUBSCRIPTION_STATUS_LABELS[readiness.status]}
                    tone={STRATEGY_SUBSCRIPTION_STATUS_TONES[readiness.status]}
                />
            </div>
            {researchSelection ? (
                <span className="text-[11px] text-yellow-300">
                    {row.isActive ? 'Included in backtests; trading inactive' : 'Select for backtests; trading inactive'}
                </span>
            ) : (
                !readiness.isStrategyGloballyActive && <span className="text-[11px] text-yellow-300">Won&apos;t trade until activated</span>
            )}
            {row.openPositionCount > 0 && <span className="text-[11px] text-zinc-500">{row.openPositionCount.toLocaleString()} open</span>}
        </div>
    );
};

/**
 * Strategy name, then its id and the entity/derivative scope on one line. Keeping the id off the name
 * line stops a wrapped name from pushing the id onto a line of its own.
 */
export const StrategyIdentityCell = ({ row }: RowProps) => {
    const { readiness, strategy } = row;

    return (
        <div className="flex flex-col gap-1.5">
            <Link className="text-sm font-medium text-primary hover:text-blue-400" to={`/user/tradingStrategy/${strategy.id}`}>
                {strategy.name}
            </Link>
            <div className="flex flex-wrap items-center gap-1">
                <span className="mr-1 font-mono text-[11px] text-zinc-500">#{strategy.id}</span>
                <StatusBadge status={describeStrategyInstrumentScope(strategy)} tone={readiness.isOptionsStrategy ? 'accent' : 'neutral'} />
                {describeAllowedEntityTypes(strategy.allowedUnderlyingEntityTypes).map((label) => (
                    <StatusBadge key={label} status={label} tone="neutral" />
                ))}
            </div>
        </div>
    );
};

/** Live/paper marker, plus a warning when the whole portfolio is switched off. */
export const PortfolioTypeBadges = ({ portfolio }: { portfolio: StrategySubscriptionPortfolio }) => (
    <>
        <StatusBadge status={portfolio.type === 'live' ? 'Live' : 'Paper'} tone={portfolio.type === 'live' ? 'info' : 'neutral'} />
        {!portfolio.active && <StatusBadge status="Portfolio inactive" tone="warning" />}
    </>
);

/** Portfolio name, then its id and type on one line, for tables that list one strategy across several portfolios. */
export const PortfolioIdentityCell = ({ row }: RowProps) => {
    const { portfolio } = row;

    return (
        <div className="flex flex-col gap-1.5">
            <Link className="text-sm font-medium text-primary hover:text-blue-400" to={`/user/portfolioSettings/${portfolio.id}`}>
                {portfolio.name}
            </Link>
            <div className="flex flex-wrap items-center gap-1">
                <span className="mr-1 font-mono text-[11px] text-zinc-500">#{portfolio.id}</span>
                <PortfolioTypeBadges portfolio={portfolio} />
            </div>
        </div>
    );
};

export const TradingProfileCell = ({ row, onConfigure }: RowProps & { onConfigure: (row: StrategySubscriptionRow) => void }) => {
    const { readiness, strategy, tradingProfileName } = row;

    if (!readiness.hasTradingProfile || tradingProfileName === null) {
        return (
            <Button className="text-yellow-400" size="sm" variant="link" onClick={() => onConfigure(row)}>
                Not linked — set up
            </Button>
        );
    }

    return row.isOwnedTradingProfile ? (
        <Link className="text-sm text-primary hover:text-blue-400" to={`/user/portfolioTradingProfile/${strategy.tradingProfileId}`}>
            {tradingProfileName}
        </Link>
    ) : (
        <span className="text-sm text-zinc-200">{tradingProfileName}</span>
    );
};

/** The resolved sizing a new entry would use, plus whether it came from the strategy or the portfolio. */
export const PositionSizingCell = ({ row }: RowProps) => {
    const { sizing } = row;

    const summary = <span className={`text-sm ${sizing.isMissing ? 'text-yellow-400' : 'text-zinc-100'}`}>{sizing.summary}</span>;

    return (
        <div className="flex flex-col gap-1">
            {sizing.ruleDescriptions.length > 1 ? <Tooltip content={sizing.ruleDescriptions.join('\n')}>{summary}</Tooltip> : summary}
            <div className="flex flex-wrap items-center gap-1.5">
                <StatusBadge status={sizing.isCustom ? 'Custom' : 'Portfolio'} tone={sizing.isCustom ? 'accent' : 'neutral'} />
                {sizing.extraRuleCount > 0 && (
                    <span className="text-[11px] text-zinc-500">+{sizing.extraRuleCount.toLocaleString()} more</span>
                )}
            </div>
        </div>
    );
};

/** OBS and exit-candidate eligibility as counts, with the selected ids on hover. */
export const DeploymentCell = ({ row }: RowProps) => {
    const { readiness, strategy } = row;
    const optionBuyingStrategyIds = strategy.optionBuyingStrategyIds ?? [];
    const exitCandidateIds = strategy.exitCandidateIds ?? [];

    return (
        <div className="flex flex-wrap gap-1">
            {readiness.isOptionsStrategy && (
                <Tooltip
                    content={
                        optionBuyingStrategyIds.length
                            ? `Option buying strategies: ${optionBuyingStrategyIds.map((id) => `#${id}`).join(', ')}`
                            : 'No option buying strategies selected'
                    }
                >
                    <StatusBadge
                        status={`${optionBuyingStrategyIds.length} OBS`}
                        tone={optionBuyingStrategyIds.length ? 'info' : 'warning'}
                    />
                </Tooltip>
            )}
            <Tooltip
                content={
                    exitCandidateIds.length
                        ? `Exit candidates: ${exitCandidateIds.map((id) => `#${id}`).join(', ')}`
                        : 'No exit candidates selected'
                }
            >
                <StatusBadge
                    status={`${exitCandidateIds.length} exit${exitCandidateIds.length === 1 ? '' : 's'}`}
                    tone={exitCandidateIds.length ? 'info' : 'warning'}
                />
            </Tooltip>
        </div>
    );
};

/** Fixed-width icon actions, so the column no longer changes width from row to row. */
export const SubscriptionActionsCell = ({ row, onConfigure, onManageScorecards, onRemove }: RowActionProps) => {
    const { readiness, strategy } = row;

    return (
        <div className="flex items-center justify-end gap-1.5">
            <Tooltip content="Configure live deployment">
                <Button aria-label={`Configure ${strategy.name}`} size="sm" variant="outline-dark" onClick={() => onConfigure(row)}>
                    <FaCog />
                </Button>
            </Tooltip>
            <Tooltip content={readiness.hasTradingProfile ? 'Manage scorecards' : 'Link a trading profile first'}>
                <Button
                    aria-label={`Manage scorecards for ${strategy.name}`}
                    disabled={!readiness.hasTradingProfile}
                    size="sm"
                    variant="outline-dark"
                    onClick={() => onManageScorecards(row)}
                >
                    <FaClipboardList />
                </Button>
            </Tooltip>
            {onRemove && (
                <Tooltip content="Remove from portfolio">
                    <DeleteButton aria-label={`Remove ${strategy.name}`} size="sm" onClick={() => onRemove(row)} />
                </Tooltip>
            )}
        </div>
    );
};
